// frontend/src/utils/idGenerator.ts
// For temporary client-side IDs only - real IDs come from backend

/**
 * Generate a temporary ID for optimistic updates
 * Format: temp-{prefix}-{timestamp}-{random}
 */
export const generateTempId = (prefix: string = 'msg'): string => {
  return `temp-${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
};

/**
 * Generate a temporary order ID for local state
 */
export const generateTempOrderId = (): string => generateTempId('order');

/**
 * Generate a temporary message ID for chat
 */
export const generateTempMessageId = (): string => generateTempId('msg');

/**
 * Generate a temporary ticket ID for support
 */
export const generateTempTicketId = (): string => generateTempId('ticket');

/**
 * Generate a temporary transaction reference
 */
export const generateTempTransactionRef = (): string => generateTempId('txn');

/**
 * Generate a temporary quick offer ID
 */
export const generateTempQuickOfferId = (): string => generateTempId('qo');

/**
 * Format an ID for display (shorten to last 8 characters)
 * Works with both old and new ID formats
 */
export const formatDisplayId = (id: string | undefined | null, length: number = 8): string => {
  if (!id) return '';
  
  // If it's in format PREFIX-YYYYMMDD-RANDOM6, show the last part
  if (id.includes('-')) {
    const parts = id.split('-');
    return parts[parts.length - 1];
  }
  
  // Otherwise show last N characters
  return id.slice(-length);
};

/**
 * Get ID prefix (e.g., ORD, TKT, CNG)
 */
export const getIdPrefix = (id: string): string | null => {
  if (!id) return null;
  
  // If it's in format PREFIX-YYYYMMDD-RANDOM6
  if (id.includes('-')) {
    return id.split('-')[0];
  }
  
  // If it's old format starting with letters (ORD123456)
  const match = id.match(/^([A-Z]+)\d+/);
  return match ? match[1] : null;
};

/**
 * Check if an ID matches the new backend format
 * Pattern: PREFIX-YYYYMMDD-RANDOM6
 */
export const isNewFormatId = (id: string): boolean => {
  if (!id) return false;
  const pattern = /^[A-Z]{3}-\d{8}-[A-Z0-9]{6}$/;
  return pattern.test(id);
};

/**
 * Check if an ID matches the old backend format
 * Pattern: PREFIX + timestamp + random
 */
export const isOldFormatId = (id: string): boolean => {
  if (!id) return false;
  return /^[A-Z]{3}\d{13,}\d{0,3}$/.test(id);
};

/**
 * Extract timestamp from new format ID
 */
export const extractTimestampFromId = (id: string): string | null => {
  if (!isNewFormatId(id)) return null;
  const parts = id.split('-');
  return parts[1]; // YYYYMMDD
};

/**
 * Get display text for ID type
 */
export const getIdTypeLabel = (id: string): string => {
  const prefix = getIdPrefix(id);
  
  switch (prefix) {
    case 'ORD': return 'Order';
    case 'TKT': return 'Support Ticket';
    case 'CNG': return 'Transaction';
    case 'LST': return 'Listing';
    case 'QO': return 'Quick Offer';
    case 'OFF': return 'Offline Payment';
    case 'COM': return 'Commitment';
    default: return 'ID';
  }
};